import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { DashboardLayout } from "@/components/dashboard/DashboardLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Plus, Pencil, Trash2, Search, CreditCard } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { fetchContracts, createContract, updateContract, deleteContract, fetchCustomers, Contract, fetchAttachments, uploadAttachment, deleteAttachment, getAttachmentDownloadUrl, Attachment } from "@/lib/reseller-api";
import { FileUpload } from "@/components/attachments/FileUpload";

const statusColors: Record<string, string> = {
  active: "bg-green-500",
  pending: "bg-yellow-500",
  cancelled: "bg-red-500",
  expired: "bg-gray-500",
};

const LANGUAGE_CURRENCY: Record<string, string> = {
  'pt-BR': 'BRL',
  'pt': 'EUR',
};

function getCurrencyForLanguage(lang: string): string {
  return LANGUAGE_CURRENCY[lang] ?? LANGUAGE_CURRENCY[lang.split('-')[0]] ?? 'EUR';
}

export default function Contracts() {
  const { t, i18n } = useTranslation();
  const currency = getCurrencyForLanguage(i18n.language);
  const [search, setSearch] = useState("");
  const [isOpen, setIsOpen] = useState(false);
  const [editing, setEditing] = useState<Contract | null>(null);
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const { data: contracts = [], isLoading } = useQuery({
    queryKey: ["contracts"],
    queryFn: fetchContracts,
  });

  const { data: customers = [] } = useQuery({
    queryKey: ["customers"],
    queryFn: fetchCustomers,
  });

  const { data: attachments = [], refetch: refetchAttachments } = useQuery({
    queryKey: ["attachments", "contract", editing?.id],
    queryFn: () => fetchAttachments("contract", editing!.id),
    enabled: !!editing?.id,
  });

  const handleUpload = async (file: File) => {
    if (!editing?.id) return;
    try {
      await uploadAttachment("contract", editing.id, file);
      await refetchAttachments();
      toast({ title: t("reseller.attachment.uploaded") });
    } catch (error: any) {
      toast({ title: t("common.error"), description: error.message, variant: "destructive" });
    }
  };

  const handleDeleteAttachment = async (attachmentId: string) => {
    try {
      await deleteAttachment(attachmentId);
      await refetchAttachments();
      toast({ title: t("reseller.attachment.deleted") });
    } catch (error: any) {
      toast({ title: t("common.error"), description: error.message, variant: "destructive" });
    }
  };

  const handleDownload = async (attachment: Attachment) => {
    try {
      const { url } = await getAttachmentDownloadUrl(attachment.id);
      window.open(url, "_blank");
    } catch (error: any) {
      toast({ title: t("common.error"), description: error.message, variant: "destructive" });
    }
  };

  const createMutation = useMutation({
    mutationFn: createContract,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["contracts"] });
      setIsOpen(false);
      toast({ title: t("reseller.contract.created") });
    },
    onError: (error: Error) => {
      toast({ title: t("common.error"), description: error.message, variant: "destructive" });
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<Contract> }) => updateContract(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["contracts"] });
      setIsOpen(false);
      setEditing(null);
      toast({ title: t("reseller.contract.updated") });
    },
    onError: (error: Error) => {
      toast({ title: t("common.error"), description: error.message, variant: "destructive" });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: deleteContract,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["contracts"] });
      toast({ title: t("reseller.contract.deleted") });
    },
    onError: (error: Error) => {
      toast({ title: t("common.error"), description: error.message, variant: "destructive" });
    },
  });

  const filtered = contracts.filter(
    (c) =>
      c.name.toLowerCase().includes(search.toLowerCase()) ||
      c.customer?.name.toLowerCase().includes(search.toLowerCase())
  );

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const formData = new FormData(e.currentTarget);
    const data = {
      name: formData.get("name") as string,
      description: formData.get("description") as string,
      customer_id: formData.get("customer_id") as string,
      billing_cycle: formData.get("billing_cycle") as Contract["billing_cycle"],
      amount: parseFloat(formData.get("amount") as string),
      currency,
      start_date: formData.get("start_date") as string,
      end_date: formData.get("end_date") as string || null,
      status: formData.get("status") as Contract["status"],
      auto_renew: formData.get("auto_renew") === "true",
    };

    if (editing) {
      updateMutation.mutate({ id: editing.id, data });
    } else {
      createMutation.mutate(data);
    }
  };

  const formatCurrency = (amount: number, cur: string) => {
    return new Intl.NumberFormat(i18n.language, { style: "currency", currency: cur }).format(amount);
  };

  return (
    <DashboardLayout>
      <div className="p-6 space-y-6">
        <div className="flex items-center justify-between">
          <h1 className="text-3xl font-bold">{t("reseller.contracts")}</h1>
          <Dialog open={isOpen} onOpenChange={(open) => { setIsOpen(open); if (!open) setEditing(null); }}>
            <DialogTrigger asChild>
              <Button><Plus className="h-4 w-4 mr-2" /> {t("reseller.contract.add")}</Button>
            </DialogTrigger>
            <DialogContent className={editing ? "max-w-3xl" : "max-w-lg"}>
              <DialogHeader>
                <DialogTitle>{editing ? t("reseller.contract.edit") : t("reseller.contract.add")}</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className={editing ? "grid grid-cols-2 gap-6" : ""}>
                  <div className="space-y-4">
                    <div className="space-y-2">
                      <Label htmlFor="name">{t("reseller.contract.name")} *</Label>
                      <Input id="name" name="name" required defaultValue={editing?.name} />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="customer_id">{t("reseller.contract.customer")} *</Label>
                      <Select name="customer_id" required defaultValue={editing?.customer_id}>
                        <SelectTrigger>
                          <SelectValue placeholder={t("reseller.contract.customer")} />
                        </SelectTrigger>
                        <SelectContent>
                          {customers.map((c) => (
                            <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="amount">{t("reseller.contract.amount")} ({currency}) *</Label>
                        <Input id="amount" name="amount" type="number" step="0.01" required defaultValue={editing?.amount} />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="billing_cycle">{t("reseller.contract.billingCycle")}</Label>
                        <Select name="billing_cycle" defaultValue={editing?.billing_cycle || "monthly"}>
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="monthly">{t("reseller.contract.cycles.monthly")}</SelectItem>
                            <SelectItem value="quarterly">{t("reseller.contract.cycles.quarterly")}</SelectItem>
                            <SelectItem value="yearly">{t("reseller.contract.cycles.yearly")}</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="start_date">{t("reseller.contract.startDate")} *</Label>
                        <Input id="start_date" name="start_date" type="date" required defaultValue={editing?.start_date} />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="end_date">{t("reseller.contract.endDate")}</Label>
                        <Input id="end_date" name="end_date" type="date" defaultValue={editing?.end_date || ""} />
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="status">{t("reseller.contract.status")}</Label>
                        <Select name="status" defaultValue={editing?.status || "active"}>
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="active">{t("reseller.contract.statuses.active")}</SelectItem>
                            <SelectItem value="pending">{t("reseller.contract.statuses.pending")}</SelectItem>
                            <SelectItem value="cancelled">{t("reseller.contract.statuses.cancelled")}</SelectItem>
                            <SelectItem value="expired">{t("reseller.contract.statuses.expired")}</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="auto_renew">{t("reseller.contract.autoRenew")}</Label>
                        <Select name="auto_renew" defaultValue={editing?.auto_renew !== false ? "true" : "false"}>
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="true">{t("reseller.common.yes")}</SelectItem>
                            <SelectItem value="false">{t("reseller.common.no")}</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                  </div>
                  {editing && (
                    <div className="space-y-2">
                      <Label>{t("reseller.attachment.title")}</Label>
                      <FileUpload
                        attachments={attachments}
                        onUpload={handleUpload}
                        onDelete={handleDeleteAttachment}
                        onDownload={handleDownload}
                      />
                    </div>
                  )}
                </div>
                <div className="space-y-2">
                  <Label htmlFor="description">{t("reseller.contract.description")}</Label>
                  <Textarea id="description" name="description" defaultValue={editing?.description || ""} />
                </div>
                <Button type="submit" className="w-full" disabled={createMutation.isPending || updateMutation.isPending}>
                  {editing ? t("common.update") : t("common.create")}
                </Button>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        <Card>
          <CardHeader>
            <div className="relative max-w-sm">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input placeholder={t("reseller.contract.searchPlaceholder")} value={search} onChange={(e) => setSearch(e.target.value)} className="pl-9" />
            </div>
          </CardHeader>
          <CardContent>
            {isLoading ? (
              <div className="text-center py-8 text-muted-foreground">{t("common.loading")}</div>
            ) : filtered.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">{t("reseller.contract.noContracts")}</div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>{t("reseller.contract.name")}</TableHead>
                    <TableHead>{t("reseller.contract.customer")}</TableHead>
                    <TableHead>{t("reseller.contract.amount")}</TableHead>
                    <TableHead>{t("reseller.contract.billingCycle")}</TableHead>
                    <TableHead>{t("reseller.contract.status")}</TableHead>
                    <TableHead>{t("reseller.contract.subscription")}</TableHead>
                    <TableHead className="w-[100px]">{t("reseller.common.actions")}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filtered.map((contract) => (
                    <TableRow key={contract.id}>
                      <TableCell className="font-medium">{contract.name}</TableCell>
                      <TableCell>{contract.customer?.name || "-"}</TableCell>
                      <TableCell>{formatCurrency(contract.amount, contract.currency)}</TableCell>
                      <TableCell className="capitalize">{t(`reseller.contract.cycles.${contract.billing_cycle}`)}</TableCell>
                      <TableCell>
                        <Badge className={statusColors[contract.status]}>{t(`reseller.contract.statuses.${contract.status}`)}</Badge>
                      </TableCell>
                      <TableCell>
                        {contract.stripe_subscription_id ? (
                          <Badge variant="outline" className="text-xs">
                            <CreditCard className="h-3 w-3 mr-1" />
                            Stripe
                          </Badge>
                        ) : (
                          <span className="text-muted-foreground text-sm">-</span>
                        )}
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <Button variant="ghost" size="icon" onClick={() => { setEditing(contract); setIsOpen(true); }}>
                            <Pencil className="h-4 w-4" />
                          </Button>
                          <Button variant="ghost" size="icon" onClick={() => { if (confirm(t("reseller.common.deleteConfirm"))) deleteMutation.mutate(contract.id); }}>
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
}
